import { FC } from "react";
import {
  OakP,
  OakFlex,
  OakSearchFilterCheckBox,
  oakDefaultTheme,
  OakThemeProvider,
  OakBox,
  OakFieldset,
} from "@oaknational/oak-components";

import {
  ExamBoard,
  SearchCheckBoxProps,
  Subject,
  UseSearchFiltersReturnType,
  YearGroup,
} from "@/context/Search/search.types";
import { toSentenceCase } from "@/node-lib/curriculum-api-2023/helpers";
import TagPromotional from "@/components/SharedComponents/TagPromotional";
import { getValidSubjectIconName } from "@/utils/getValidSubjectIconName";
import { TrackSearchModifiedProps } from "@/components/TeacherViews/Search/helpers";
import { FilterTypeValueType } from "@/browser-lib/avo/Avo";

type SearchFiltersProps = UseSearchFiltersReturnType & {
  isMobileFilter?: boolean;
  trackSearchModified: (props: TrackSearchModifiedProps) => void;
};

const renderFilterSection = (
  title: string,
  filters: ((ExamBoard | YearGroup | Subject) & SearchCheckBoxProps)[],
  hasIcon: boolean,
  isLast: boolean,
  trackSeachModified: (props: TrackSearchModifiedProps) => void,
  filterType: FilterTypeValueType,
  isMobileFilter?: boolean,
) => (
  <OakBox
    $mb="spacing-32"
    $bb={!isLast ? "border-solid-s" : null}
    $borderColor={!isLast ? "grey40" : null}
  >
    <OakFieldset>
      <OakP as={"legend"} $mb="spacing-24" $font={"heading-7"}>
        {title}
      </OakP>
      <OakFlex
        $gap={"spacing-12"}
        $mb="spacing-32"
        $flexDirection={"row"}
        $flexWrap={"wrap"}
      >
        {filters.map((filter) => (
          <OakSearchFilterCheckBox
            name={`${title.toLowerCase()}Filters`}
            displayValue={filter.title}
            key={`search-filters-${title.toLowerCase()}-${filter.slug}`}
            aria-label={`${filter.title} filter`}
            id={`search-filters-${title.toLowerCase()}-${
              filter.slug
            }:mobile:${isMobileFilter}`}
            value={`${title} filter`}
            {...filter}
            icon={hasIcon ? getValidSubjectIconName(filter.slug) : undefined}
            onChange={() => {
              trackSeachModified({
                checked: filter.checked,
                filterType,
                filterValue: filter.title,
                searchFilterMatchType: "default",
              });
              filter.onChange();
            }}
          />
        ))}
      </OakFlex>
    </OakFieldset>
  </OakBox>
);

const SearchFilters: FC<SearchFiltersProps> = (props) => {
  const {
    keyStageFilters,
    yearGroupFilters,
    subjectFilters,
    examBoardFilters,
    isMobileFilter,
    legacyFilter,
    trackSearchModified,
  } = props;

  return (
    <OakThemeProvider theme={oakDefaultTheme}>
      <OakBox $mb="spacing-32" $bb={"border-solid-s"} $borderColor={"grey40"}>
        <OakFieldset>
          <OakP as={"legend"} $mb="spacing-24" $font={"heading-7"}>
            Curriculum
          </OakP>
          <OakFlex
            $gap={"spacing-16"}
            $mb="spacing-32"
            $flexDirection={"column"}
            $flexWrap={"wrap"}
          >
            <OakSearchFilterCheckBox
              name={"new"}
              displayValue={"Show new only"}
              key={`search-filters-curriculum-filter`}
              aria-label={`Show new content filter`}
              aria-describedby={
                isMobileFilter
                  ? "legacyFilterHintIdMobile"
                  : "legacyFilterHintId"
              }
              {...legacyFilter}
              id={`search-filters-showNewContent:mobile:${isMobileFilter}`}
              value="new"
              onChange={() => {
                trackSearchModified({
                  checked: legacyFilter.checked,
                  filterType: "Lesson Cohort filter",
                  filterValue: "2023-2026",
                  searchFilterMatchType: "default",
                });
                legacyFilter.onChange();
              }}
            />
            <OakFlex $alignItems={"flex-start"}>
              <OakBox>
                <TagPromotional size={"small"} />
              </OakBox>
              <OakP
                $font={"body-3"}
                $wordWrap={"normal"}
                $color={"text-subdued"}
                id={
                  isMobileFilter
                    ? "legacyFilterHintIdMobile"
                    : "legacyFilterHintId"
                } // pa11y error for non unique id when mobile and desktop views rendered simultaneously
              >
                Resources designed for the classroom
              </OakP>
            </OakFlex>
          </OakFlex>
        </OakFieldset>
      </OakBox>
      <OakBox $mb="spacing-32" $bb={"border-solid-s"} $borderColor={"grey40"}>
        <OakFieldset>
          <OakP as={"legend"} $mb="spacing-24" $font={"heading-7"}>
            Key stages
          </OakP>
          <OakFlex
            $gap={"spacing-12"}
            $mb="spacing-32"
            $flexDirection={"row"}
            $flexWrap={"wrap"}
          >
            {keyStageFilters.map((keyStageFilter) => (
              <OakSearchFilterCheckBox
                name={"keyStageFilters"}
                displayValue={
                  keyStageFilter.shortCode === "EYFS"
                    ? keyStageFilter.shortCode
                    : toSentenceCase(keyStageFilter.title)
                }
                key={`search-filters-keyStage-${keyStageFilter.slug}`}
                aria-label={`${keyStageFilter.title} filter`}
                id={`search-filters-keyStage-${keyStageFilter.slug}:mobile:${isMobileFilter}`}
                value="Key stage filter"
                checked={keyStageFilter.checked}
                onChange={() => {
                  trackSearchModified({
                    checked: keyStageFilter.checked,
                    filterType: "Key stage filter",
                    filterValue: keyStageFilter.title,
                    searchFilterMatchType: "default",
                  });
                  keyStageFilter.onChange();
                }}
              />
            ))}
          </OakFlex>
        </OakFieldset>
      </OakBox>
      <>
        {renderFilterSection(
          "Years",
          yearGroupFilters,
          false,
          false,
          trackSearchModified,
          "Year filter",
          isMobileFilter,
        )}
        {renderFilterSection(
          "Exam Boards",
          examBoardFilters,
          false,
          false,
          trackSearchModified,
          "Exam board filter",
          isMobileFilter,
        )}
        {renderFilterSection(
          "Subjects",
          subjectFilters,
          false,
          true,
          trackSearchModified,
          "Subject filter",
          isMobileFilter,
        )}
      </>
    </OakThemeProvider>
  );
};

export default SearchFilters;
